import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { ArrowLeft, Save, Shield, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useOrgId } from '@/hooks/useOrgContext';

const DEA_SCHEDULES = [
  'Non-controlled',
  'Schedule II',
  'Schedule III',
  'Schedule IV',
  'Schedule V',
];

const DOSAGE_FORMS = [
  'Tablet',
  'Capsule',
  'Injectable',
  'Solution',
  'Suspension',
  'Cream',
  'Ointment',
  'Patch',
  'Inhaler',
  'Suppository',
  'Other',
];

export default function MedicationCreateRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const org_id = useOrgId(); // Get org_id from context

  const [formData, setFormData] = useState({
    drug_name: '',
    generic_name: '',
    brand_name: '',
    ndc_code: '',
    strength: '',
    dosage_form: 'Injectable',
    dea_schedule: 'Non-controlled',
    lot_number: '',
    expiration_date: '',
    quantity_on_hand: 0,
    unit_of_measure: 'vials',
    par_level_min: null as number | null,
    par_level_max: null as number | null,
    unit_cost: 0,
    storage_location: '',
    storage_requirements: '',
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: async () => {
      // Validate required fields
      const newErrors: Record<string, string> = {};
      if (!formData.drug_name) newErrors.drug_name = 'Drug name is required';
      if (!formData.generic_name) newErrors.generic_name = 'Generic name is required';
      if (!formData.ndc_code) newErrors.ndc_code = 'NDC code is required';
      if (!formData.strength) newErrors.strength = 'Strength is required';
      if (!formData.storage_location) newErrors.storage_location = 'Storage location is required';
      
      // Validate NDC format (basic check: 5-4-2 or 5-3-2 or similar)
      if (formData.ndc_code && !formData.ndc_code.match(/^\d{5}-\d{3,4}-\d{1,2}$/)) {
        newErrors.ndc_code = 'NDC format should be XXXXX-XXXX-XX or XXXXX-XXX-XX';
      }

      // Validate par levels
      if (formData.par_level_min !== null && formData.par_level_max !== null) {
        if (formData.par_level_min > formData.par_level_max) {
          newErrors.par_level_min = 'Min cannot exceed max';
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        throw new Error('Validation failed');
      }

      if (!org_id) {
        setErrors({ form: 'Organization context not loaded yet — try again in a moment.' });
        throw new Error('Missing org context');
      }

      // Insert medication
      const { data: medication, error: medError } = await supabase
        .from('or_medications')
        .insert({
          org_id, // From useOrgId() hook — null-guarded above
          ...formData,
          brand_name: formData.brand_name || null,
          lot_number: formData.lot_number || null,
          expiration_date: formData.expiration_date || null,
          storage_requirements: formData.storage_requirements || null,
          notes: formData.notes || null,
        })
        .select()
        .single();

      if (medError) throw medError;

      // If initial quantity > 0, create a "Received" transaction
      if (formData.quantity_on_hand > 0) {
        const { error: txError } = await supabase
          .from('or_medication_transactions')
          .insert({
            medication_id: medication.medication_id,
            transaction_type: 'Received',
            quantity_change: formData.quantity_on_hand,
            quantity_after: formData.quantity_on_hand,
            lot_number: formData.lot_number || null,
            notes: 'Initial inventory',
            performed_by: user?.id,
          });

        if (txError) throw txError;
      }

      return medication;
    },
    onSuccess: (medication) => {
      queryClient.invalidateQueries({ queryKey: ['or-medications'] });
      navigate(`/medications/${medication.medication_id}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    createMutation.mutate();
  };

  const handleChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const isControlled = formData.dea_schedule !== 'Non-controlled';
  const totalValue = formData.quantity_on_hand * formData.unit_cost;

  return (
    <div className="min-h-full bg-pt-darker text-pt-text p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/medications')}
          className="p-2 hover:bg-pt-panel/50 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-pt-text mb-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            Add New Medication
          </h1>
          <p className="text-pt-text-muted text-sm">Create a new medication inventory record</p>
        </div>
      </div>

      {/* Controlled Substance Warning */}
      {isControlled && (
        <div className="bg-pt-red/10 border border-pt-red/30 rounded-lg p-4 mb-6 flex items-start gap-3">
          <Shield className="text-pt-red mt-0.5" size={20} />
          <div>
            <div className="text-pt-red font-semibold mb-1">DEA Controlled Substance</div>
            <div className="text-pt-red text-sm">
              {formData.dea_schedule} — This medication will require witness verification for all transactions.
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="max-w-5xl space-y-6">
        {/* Medication Information */}
        <div className="bg-pt-dark/30 border border-pt-border rounded-lg p-6">
          <h2 className="text-lg font-semibold text-pt-text mb-4">Medication Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Drug Name *
              </label>
              <input
                type="text"
                value={formData.drug_name}
                onChange={(e) => handleChange('drug_name', e.target.value)}
                className={`w-full px-3 py-2 bg-pt-darker/50 border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50 ${
                  errors.drug_name ? 'border-pt-red/50' : 'border-pt-border'
                }`}
                placeholder="e.g., Fentanyl Citrate"
              />
              {errors.drug_name && (
                <div className="mt-1 text-xs text-pt-red">{errors.drug_name}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Generic Name *
              </label>
              <input
                type="text"
                value={formData.generic_name}
                onChange={(e) => handleChange('generic_name', e.target.value)}
                className={`w-full px-3 py-2 bg-pt-darker/50 border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50 ${
                  errors.generic_name ? 'border-pt-red/50' : 'border-pt-border'
                }`}
                placeholder="e.g., Fentanyl"
              />
              {errors.generic_name && (
                <div className="mt-1 text-xs text-pt-red">{errors.generic_name}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Brand Name
              </label>
              <input
                type="text"
                value={formData.brand_name}
                onChange={(e) => handleChange('brand_name', e.target.value)}
                className="w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
                placeholder="e.g., Sublimaze"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                NDC Code *
              </label>
              <input
                type="text"
                value={formData.ndc_code}
                onChange={(e) => handleChange('ndc_code', e.target.value)}
                className={`w-full px-3 py-2 bg-pt-darker/50 border rounded-lg text-pt-text font-mono focus:outline-none focus:border-pt-cyan/50 ${
                  errors.ndc_code ? 'border-pt-red/50' : 'border-pt-border'
                }`}
                placeholder="00409-1639-01"
              />
              {errors.ndc_code && (
                <div className="mt-1 text-xs text-pt-red">{errors.ndc_code}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Strength *
              </label>
              <input
                type="text"
                value={formData.strength}
                onChange={(e) => handleChange('strength', e.target.value)}
                className={`w-full px-3 py-2 bg-pt-darker/50 border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50 ${
                  errors.strength ? 'border-pt-red/50' : 'border-pt-border'
                }`}
                placeholder="e.g., 50mcg/mL"
              />
              {errors.strength && (
                <div className="mt-1 text-xs text-pt-red">{errors.strength}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Dosage Form *
              </label>
              <select
                value={formData.dosage_form}
                onChange={(e) => handleChange('dosage_form', e.target.value)}
                className="w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
              >
                {DOSAGE_FORMS.map((form) => (
                  <option key={form} value={form}>
                    {form}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                DEA Schedule *
              </label>
              <select
                value={formData.dea_schedule}
                onChange={(e) => handleChange('dea_schedule', e.target.value)}
                className="w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
              >
                {DEA_SCHEDULES.map((schedule) => (
                  <option key={schedule} value={schedule}>
                    {schedule}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Lot & Expiration */}
        <div className="bg-pt-dark/30 border border-pt-border rounded-lg p-6">
          <h2 className="text-lg font-semibold text-pt-text mb-4">Lot & Expiration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Lot Number
              </label>
              <input
                type="text"
                value={formData.lot_number}
                onChange={(e) => handleChange('lot_number', e.target.value)}
                className="w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text font-mono focus:outline-none focus:border-pt-cyan/50"
                placeholder="LOT2024-F001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Expiration Date
              </label>
              <input
                type="date"
                value={formData.expiration_date}
                onChange={(e) => handleChange('expiration_date', e.target.value)}
                className="w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
              />
            </div>
          </div>
        </div>

        {/* Inventory */}
        <div className="bg-pt-dark/30 border border-pt-border rounded-lg p-6">
          <h2 className="text-lg font-semibold text-pt-text mb-4">Inventory Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Initial Quantity
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={formData.quantity_on_hand}
                onChange={(e) => handleChange('quantity_on_hand', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Unit of Measure
              </label>
              <input
                type="text"
                value={formData.unit_of_measure}
                onChange={(e) => handleChange('unit_of_measure', e.target.value)}
                className="w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
                placeholder="vials, ampules, tablets, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Par Level Min
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={formData.par_level_min || ''}
                onChange={(e) => handleChange('par_level_min', e.target.value ? parseInt(e.target.value) : null)}
                className={`w-full px-3 py-2 bg-pt-darker/50 border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50 ${
                  errors.par_level_min ? 'border-pt-red/50' : 'border-pt-border'
                }`}
              />
              {errors.par_level_min && (
                <div className="mt-1 text-xs text-pt-red">{errors.par_level_min}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Par Level Max
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={formData.par_level_max || ''}
                onChange={(e) => handleChange('par_level_max', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Unit Cost
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.unit_cost}
                onChange={(e) => handleChange('unit_cost', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
              />
            </div>
          </div>
        </div>

        {/* Storage */}
        <div className="bg-pt-dark/30 border border-pt-border rounded-lg p-6">
          <h2 className="text-lg font-semibold text-pt-text mb-4">Storage Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Storage Location *
              </label>
              <input
                type="text"
                value={formData.storage_location}
                onChange={(e) => handleChange('storage_location', e.target.value)}
                className={`w-full px-3 py-2 bg-pt-darker/50 border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50 ${
                  errors.storage_location ? 'border-pt-red/50' : 'border-pt-border'
                }`}
                placeholder="OR Pyxis Station 1, OR Refrigerator, etc."
              />
              {errors.storage_location && (
                <div className="mt-1 text-xs text-pt-red">{errors.storage_location}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-pt-text mb-2">
                Storage Requirements
              </label>
              <input
                type="text"
                value={formData.storage_requirements}
                onChange={(e) => handleChange('storage_requirements', e.target.value)}
                className="w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
                placeholder="Refrigerate 2-8°C, Room temperature, etc."
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-pt-dark/30 border border-pt-border rounded-lg p-6">
          <h2 className="text-lg font-semibold text-pt-text mb-4">Notes</h2>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text focus:outline-none focus:border-pt-cyan/50"
            placeholder="Additional notes, warnings, or special instructions..."
          />
        </div>

        {/* Summary Card */}
        <div className="bg-pt-dark/30 border border-pt-cyan/30 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-pt-text mb-3">Summary</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-pt-text-muted">Initial Quantity:</span>
              <span className="ml-2 text-pt-text font-medium">
                {formData.quantity_on_hand} {formData.unit_of_measure}
              </span>
            </div>
            <div>
              <span className="text-pt-text-muted">Unit Cost:</span>
              <span className="ml-2 text-pt-text font-medium">${formData.unit_cost.toFixed(2)}</span>
            </div>
            <div>
              <span className="text-pt-text-muted">Total Value:</span>
              <span className="ml-2 text-pt-gold font-bold">${totalValue.toFixed(2)}</span>
            </div>
            <div>
              <span className="text-pt-text-muted">DEA Status:</span>
              <span className={`ml-2 font-medium ${isControlled ? 'text-pt-red' : 'text-pt-text'}`}>
                {formData.dea_schedule}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/medications')}
            className="px-6 py-2 bg-pt-panel hover:bg-pt-panel text-pt-text rounded-lg font-semibold transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-6 py-2 bg-pt-green hover:bg-pt-green text-pt-text rounded-lg font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <Save size={18} />
            {createMutation.isPending ? 'Creating...' : 'Create Medication'}
          </button>
        </div>

        {createMutation.isError && (
          <div className="bg-pt-red/10 border border-pt-red/30 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="text-pt-red mt-0.5" size={20} />
            <div className="text-pt-red text-sm">
              {createMutation.error?.message || 'Failed to create medication'}
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
